import { ContactPaymentContextKey } from "../context/contacts/ContactsProvider";
import { Entity, Role } from "./constants";
import { CurrencyKey } from "./useCurrency";

export interface EtlHotspot {
  id: string;
  block_added: number;
  block_height: number;
  entity: "hotspot";
  last_poc_challenge: number;
  last_update: number;
  place: string | null;
  lat: number;
  lng: number;
  location: string;
  name: string;
  online: boolean;
  owner: string;
  payer: string;
  relayed: boolean;
  reward_scale: number;
  timestamp_added: number;
  last_assertion: number | null;
  elevation: number;
  gain: number;
  nonce: number;
  deny_list: boolean;
  tx_reward_unit: number;
  rx_reward_unit: number;
  poc_reward_score: number;
  witness_score: number;
}

export interface PhpHotspot extends EtlHotspot {
  total_messages: number;
  verified_by: string | null;
}

export type Hotspot = PhpHotspot | EtlHotspot;

export const isPhpHotspot = (hotspot: Hotspot): hotspot is PhpHotspot =>
  "total_messages" in hotspot && typeof hotspot.total_messages === "number";

export interface HotspotLocation {
  uuid: string;
  created_by: string;
  created_at: string;
  updated_by: string | null;
  updated_at: string;
  name: string;
  address: string | null;
  lat: number;
  lng: number;
  h3_index_r12: string;
  h3_index_r4: string;
  metadata: string | null;
  linked_contact_ids: string[];
  height: number;
  super_simulations: SuperSimulation[];
  comments: Comment[];
}

export interface Contact {
  uuid: string;
  created_by: string;
  created_at: string;
  updated_by: string | null;
  updated_at: string;
  name: string;
  email: string | null;
  phone: string | null;
  payment_methods: PaymentMethod[];
  linked_location_ids: string[];
  vat: string | null;
  contact_payments: ContactPayment[];
  comments: Comment[];
  total_messages: number | null;
}

export interface Install {
  uuid: string;
  created_by: string;
  created_at: string;
  updated_by: string | null;
  updated_at: string;
  hotspot_uuid: string;
  location_uuid: string;
  installed_at: string;
  uninstalled_at: string | null;
  commissions: InstallCommission[];
}

export type WalletVerificationStatus = "unverified" | "pending" | "completed";

export interface WalletVerification {
  status: WalletVerificationStatus;
  donation_wallet: string;
  verification_amount: number;
  expires_at: string;
  verified_at: string | null;
}

export interface Wallet {
  uuid: string;
  address: string;
  created_by: string;
  created_at: string;
  updated_by: string | null;
  updated_at: string;
  verified: boolean;
  tracked: boolean;
  current_verification_status: WalletVerificationStatus;
  verifications: WalletVerification[];
  balance_data: {
    balance: number;
    dc_balance: number;
    iot_balance: number;
    mobile_balance: number;
    staked_balance: number;
  };
  hotspot_count: number;
}

export interface Member {
  name: string;
  email: string;
  uuid: string | null;
  avatar_url: string;
  pending: boolean;
  role: Role;
  deleted_at: string | null;
}

export interface Permission {
  label: string;
  description: string;
}

export interface Downgrade {
  date_utc: number;
  date: string;
  current: {
    plan: {
      id: string;
      name: string;
      uuid: string;
      price: number;
      quantity: number;
    };
    "pre-paid": {
      hotspot: {
        id: string;
        name: string;
        uuid: string;
        quantity: number;
      };
      simulation: {
        id: string;
        name: string;
        uuid: string;
        quantity: number;
      };
    };
  };
  future: {
    plan: {
      id: string;
      name: string;
      uuid: string;
      price: number;
      quantity: number;
    };
    "pre-paid": {
      hotspot: {
        id: string;
        name: string;
        uuid: string;
        quantity: number;
      };
      simulation: {
        id: string;
        name: string;
        uuid: string;
        quantity: number;
      };
    };
  };
}

export interface Workspace {
  uuid: string;
  channel_id: string | null;
  name: string;
  type: string;
  updated_at: string;
  created_at: string;
  created_by: string;
  onboarded_at: string;
  tax_type: string | null;
  tax_value: string | null;
  timezone: string;
  downgrade: Downgrade | null;
  billing_details: {
    city: string | null;
    country: string | null;
    line1: string | null;
    line2: string | null;
    postal_code: string | null;
    state: string | null;
    billing_name: string | null;
    billing_email: string | null;
  };
  permissions: {
    "bulk-import-locations": boolean;
    "bulk-payments": boolean;
    "elevation-contours": boolean;
    "export-hotspot-rewards": boolean;
    "host-dashboard": boolean;
    "hotspotty-discord-pro": boolean;
    "line-of-sight-tool": boolean;
    "regional-dashboard": boolean;
    "rewards-by-type": boolean;
    "satellite-view": boolean;
    "time-travel-simulations": boolean;
    "too-close-to-witness": boolean;
    "transmit-scale-placements": boolean;
    "upload-pictures-and-documents": boolean;
  };
  active_plan: {
    uuid: string;
    group: "plan" | "pre-paid";
    type: PlanType;
    onboarded_at: string;
    on_grace: boolean;
    status: string;
    next_billing_date: string;
    next_billing_amount: string;
    simulations_used_on_period: number;
    allowed_hotspots: number;
    allowed_simulations: number;
    allowed_team_members: number;
    allowed_locations: number;
    requires_action?: "authentication" | string;
    requires_action_link?: string;
  };
  counts: {
    location: number;
    hotspot: number;
    member: number;
    install: number;
    wallet: number;
    commissionReport: number;
    contact: number;
  };
}

export interface Coupon {
  id?: string;
  name?: string;
  valid?: boolean;
  amount_off?: number;
  currency?: string;
  percent_off?: number;
}

export interface Label {
  uuid: string;
  workspaceUuid: string;
  type: Entity;
  name: string; // TODO: title or name?
  title?: string;
  color: string;
  order: number;
  order_column?: number;
  link?: string;
  Icon?: React.ComponentType<any>;
}

export interface GenericFunction<TArgs, TRet> {
  (...args: TArgs[]): TRet | void;
}

export interface DataById<T> {
  [id: string]: T;
}

export type CommentType =
  | "hotspot"
  | "location"
  | "contact"
  | "install"
  | "commission-report";

export interface Comment {
  uuid: string;
  user_uuid: string;
  user_name: string;
  created_at: string;
  message: string;
  system: boolean;
  updated_at: string;
}

export interface MessageUserData {
  avatar_url: string;
  name: string;
  provider_nickname: string;
  uuid: string;
}

export interface Message {
  uuid: string;
  message: string;
  status: string;
  payload: string;
  response: string;
  sent_at: string;
  created_at: string;
  updated_at: string;
  user_avatar: null;
  user_data: MessageUserData | null;
  contact_name: string | null;
  contact_uuid: string | null;
  user_email: string | null;
  user_uuid: string | null;
}

export interface PlaceResult {
  id: string;
  type: "Feature";
  place_type: string[];
  relevance: number;
  properties: { short_code?: string };
  text: string;
  text_en: string;
  place_name_en: string;
  place_name: string;
  center: [number, number];
  geometry: {
    type: "Point";
    coordinates: [number, number];
    interpolated?: boolean;
  };
  address?: string;
  context: Array<{
    id: string;
    text: string;
    text_en: string;
    short_code?: string;
  }>;
}

export const isPlaceResult = (place: unknown): place is PlaceResult =>
  typeof place === "object" &&
  place !== null &&
  place &&
  "id" in place &&
  "type" in place &&
  "text" in place &&
  "place_type" in place &&
  "relevance" in place &&
  "geometry" in place;

export interface PlacesSearchResponse {
  type: "FeatureCollection";
  query: string[];
  features: PlaceResult[];
  attribution: string;
}

export interface DiscordChannel {
  discord_id: string;
  name: string;
  type: Entity;
  linked_id: string;
  h3_index_r4: string;
  created_by: string;
}

export type FetchStatus = "fetching" | "ready";

export enum PaymentTypeKey {
  paypal = "paypal",
  iban = "iban",
  walletAddress = "walletAddress",
  venmo = "venmo",
  cashApp = "cashApp",
  cash = "cash",
  wiseByEmail = "wiseByEmail",
  other = "other"
}

export interface PaymentMethod {
  contact_id?: number;
  uuid?: string | null;
  name: string;
  type: PaymentTypeKey;
  currency: CurrencyKey;
  metadata: string;
  primary: boolean;
  updated_at?: string | null;
  created_at?: string | null;
  deleted_at?: string | null;
}

export interface Placement {
  uuid: string;
  placementable_id: string;
  placementable_type: "hotspot" | "location";
  lat: number;
  lng: number;
  h3_index_r12: string;
  h3_index_r5: string;
  created_by: string;
  created_at: string;
  updated_by: string;
  updated_at: string;
}

export enum CommissionType {
  percentage = "percentage",
  recurringFixed = "recurring-fixed",
  oneTimeFixed = "one-time-fixed"
}

export enum CommissionFrequency {
  monthly = "monthly"
}

export interface InstallCommission {
  uuid?: string | null;
  contact_uuid: string;
  type: CommissionType;
  value: number;
  currency: CurrencyKey;
  template_uuid: string | null;
  start_date: string;
  end_date?: string | null;
  updated_at?: string | null;
  created_at?: string | null;
  deleted_at?: string | null;
  cron_expression?: string | null;
}

export interface CommissionTemplate {
  uuid: string;
  name: string;
  type: CommissionType;
  cron_expression?: string | null;
  value: number;
  currency: CurrencyKey;
  primary: boolean;
  workspaceUuid: string;
  order: number;
}

export type CommissionSubset = Pick<
  InstallCommission,
  | "uuid"
  | "currency"
  | "type"
  | "cron_expression"
  | "value"
  | "start_date"
  | "end_date"
  | "template_uuid"
>;

export interface ContactCommission {
  commission: CommissionSubset;
  totalCommissionValue: number;
  install: {
    uuid: string;
    installed_at: string | null;
    uninstalled_at: string | null;
    hotspot: {
      uuid: string;
      name: string;
    };
    location: {
      uuid: string;
      name: string;
    };
  };
}

export interface CommissionValues {
  timestamp: number;
  currency: CurrencyKey;
  amount: number;
  tx_hash: string | null;
  block: number | null;
}

export interface ContactCommissions {
  contact: Pick<Contact, "uuid" | "name">;
  totalCommissionValue: number;
  commissions: ContactCommission[];
}

export interface CommissionReport {
  uuid: string;
  created_at: string;
  created_by: string | null;
  deleted_at: string | null;
  start_date: string; //  "2021-10-01",
  end_date: string; // "2021-10-28",
  timezone: string;
  currency: CurrencyKey;
  total_commission_amount: number; // Total commissions amount for that period in the currency of the report
  report: ContactCommissions[];
  identifier: number;
  current_block: number | null;
  status: "pending" | "completed" | "failed";
  title: string | null;
  contact_payments: ContactPayment[];
  contacts: Contact[];
  legacy_report: string | null;
  time: number;
}

export interface ContactPayment {
  uuid: string;
  currency: CurrencyKey;
  amount: number;
  created_by: string;
  updated_at: string;
  created_at: string;
  report_commission_uuid: string | null;
  workspace_uuid: string;
  contact_uuid: string;
  context: ContactPaymentContextKey;
  transaction_hash: string | null;
  notes: string | null;
  paid_at: string;
}

export interface PaymentTransaction {
  hash: string;
  payer: string;
  block: number;
  time: number;
  total_amount: number;
  status: "pending" | "failed" | "cleared";
  payments: {
    payee: string;
    amount: number;
    memo: string;
  }[];
}

export interface CommissionPaymentStatus {
  contactPayment?: ContactPayment | undefined;
  paymentTransaction?: PaymentTransaction | undefined;
  status: "failed" | "paid" | "unpaid" | "loading";
}

export interface HotspotMaker {
  maker_id: string;
  maker_name: string;
  all_hotspots: {
    total: number;
    online: number;
    offline: number;
    synced: number;
    not_synced: number;
  };
  installed_hotspots: {
    total: number;
    online: number;
    offline: number;
    synced: number;
    not_synced: number;
  };
}

export interface SuperSimulationData {
  lu: string; // location uuid
  simulationId: string;
  h3r12: string;
  lat: number;
  lng: number;
  h: number; // installation height
  vh: string[]; // visible hotspot ids
  vhc: number; // visible hotspots count
  vl: []; // Visible location ids
  vlc: number; // Visible locations count
  txru: number; // tx reward units (HIP15)
  rxru: number; // rx reward units (HIP15)
  ps: number; // poc score
}

export interface SuperSimulation {
  location_uuid: string;
  status: "pending" | "completed" | "failed";
  batch_identifier: string | null;
  simulation_report: SuperSimulationData | null;
  uuid: string;
  updated_at: string;
  created_at: string;
  height: number;
  payload: {
    id: number;
    locations: Array<{
      h: number; // height
      lat: number;
      lng: number;
      uuid: string;
    }>;
  };
}

export enum PlanType {
  community = "community",
  pro = "pro",
  business = "business",
  prePaidHotspot = "hotspot",
  prePaidSimulation = "simulation"
}

export interface NewPlan {
  plan?: Plan;
  hotspotAmount: number;
  simulationAmount: number;
  hotspotAmountPrice: number;
  simulationAmountPrice: number;
  trial: number;
  total: string;
  coupon: Coupon;
  discount: string;
  paymentMethod: CardPaymentMethod | null | undefined;
  cardHolderName: string;
}

export interface PriceTiers {
  unit_amount: number;
  up_to: number | null;
}

export interface Plan {
  uuid: string;
  name: string;
  price_type: string;
  price: number | null;
  price_tiers: PriceTiers[] | null;
  currency: string;
  type: PlanType;
  group: "plan" | "pre-paid";
  features: string[] | null;
  stripe_product_id: string;
  stripe_price_id: string;
  order: number;
  description: string | null;
  detailed_features: PlanFeaturesMap | null;
  hotspots: number;
  simulations: number;
}

export interface PlanFeaturesMap {
  [key: string]: {
    [key: string]: { title: string; url: string; hidden: boolean };
  };
}

export interface Invoice {
  id: string;
  number: string;
  date: string;
  card: string;
  last4: string;
  amount: number;
  pdf: string;
}

export enum PaymentStep {
  plans = 0,
  choosePlan = 1,
  featuresByDemand = 2,
  paymentMethod = 3,
  paymentReview = 4,
  paymentResponse = 5
}

export interface CardPaymentMethod {
  id: string;
  card: {
    address: {
      city: string;
      country: string;
      line1: string;
      line2: string;
      postal_code: string;
      state: string;
    };
    brand: string;
    name: string;
    email: string;
    exp_month: number;
    exp_year: number;
    last4: string;
  };
}

export interface StripeTax {
  country: string;
  format: string;
  placeholder: string;
  external_type: string;
  verifiable: boolean;
  description: string;
  category?: string;
  replacement_string?: string;
  only_replace_if_group_captured?: number;
}

export type Provider = "google" | "discord" | "apple";
